import { useEffect, useRef } from 'react';
import classnames from 'classnames';

import style from './Popup.module.scss';

import { ClickOutsideMask } from '@components/ClickOutsideMask';
import { Modal } from '@components/Modal';
import { CrossIcon } from '@components/ActionButtons/CrossIcon';

export interface PopupProps {
    children?: React.ReactNode;
    modifiers?: PopupModifiers[];
    theme?: PopupTheme;
    transparentBackground?: boolean;
    stopPointerPropagation?: boolean;
    onOutOfContentClick?(): void;
    onCrossIconClick?(): void;
}

export enum PopupTheme {
    // @ts-ignore
    Primary = style.grayBackground,
    // @ts-ignore
    Gray = style.gray,
}

export enum PopupModifiers {
    // @ts-ignore
    PositionLeft = style.root_positionLeft,
    // @ts-ignore
    Redesign = style.root_redesign,
    // @ts-ignore
    FromTop = style.root_fromTop,
    // @ts-ignore
    NoPaddings = style.noPaddings,
    // @ts-ignore
    SamePaddings = style.samePaddings,
}

interface Props extends PopupProps {}

export const Popup = ({
    children,
    modifiers = [],
    theme,
    transparentBackground = true,
    stopPointerPropagation = false,
    onOutOfContentClick,
    onCrossIconClick,
}: Props) => {
    const rootRef = useRef<HTMLDivElement>(null);

    // Попап рендерится порталом в #modal-root. Если он открыт поверх правого меню
    // (ClickOutsideAlerter слушает document-mousedown), клик по попапу закрыл бы меню.
    // Гасим всплытие pointer-событий, когда это явно запрошено вызывающим.
    useEffect(() => {
        const node = rootRef.current;

        if (!stopPointerPropagation || !node) {
            return undefined;
        }

        const stopPropagation = (event: Event): void => event.stopPropagation();

        node.addEventListener('mousedown', stopPropagation);
        node.addEventListener('touchstart', stopPropagation);

        return () => {
            node.removeEventListener('mousedown', stopPropagation);
            node.removeEventListener('touchstart', stopPropagation);
        };
    }, [stopPointerPropagation]);

    return (
        <Modal>
            <div
                ref={rootRef}
                className={classnames([style.root, theme, ...modifiers])}
                data-app-popup="" // Marks popup, can be used by other components to determine if a click happened inside it or not
            >
                <ClickOutsideMask
                    transparentBackground={transparentBackground}
                    onClickOutside={onOutOfContentClick}
                />

                <div className={style.content}>
                    {children}
                    {onCrossIconClick && <CrossIcon onClose={onCrossIconClick} />}
                </div>
            </div>
        </Modal>
    );
};
